import { SocialSignupButtons } from '@h2hspark/auth-ui';
import {
  MEMBER_INTEREST_TAG_SELECTION,
  MEMBER_REGISTRATION_FIELD_LIMITS,
  MEMBER_REGISTRATION_VOCABULARY_UNAVAILABLE as NOT_READY,
  type MemberRegistrationAnswers,
} from '@h2hspark/contracts';
import { SelectableCard, Skeleton, ToggleChip } from '@h2hspark/ui';
import { Check } from 'lucide-react';
import * as React from 'react';

import type { RegistrationVocabularies } from './api';
import {
  REGISTRATION_PASSWORD_RULE,
  REGISTRATION_SIGNED_IN_LABEL,
  REGISTRATION_SIGNED_IN_NOTE,
} from './constants';
import { MatchingFieldsPanel } from './matching-fields-panel';

/**
 * The five steps' fields, transcribed from `Member App.dc.html`'s `showRegister` block
 * (lines 64–128), `rs0`…`rs4`.
 *
 * ## The measurements, per step, next to the step they came from
 *
 * `docs/standards/building-from-designs.md` asks for the transcription to sit with the surface
 * rather than in a shared table, because this app's numbers differ from both consoles' by amounts
 * that look like rounding. These are the member app's.
 *
 * ### Shared by every step
 *
 * | Element | Prototype | Here |
 * | --- | --- | --- |
 * | Body container | `flex:1; padding:16px 20px` | `flex-1 px-5 py-4` |
 * | `h1` | `margin:0 0 6px; font-size:var(--text-2xl)` | `mb-[6px] text-2xl` |
 * | Subtitle | `--text-md; --text-muted` | `text-md text-muted-foreground` |
 * | Field label | `font-brand; --text-sm; weight 600; --text-strong; margin-bottom:7px` | `mb-[7px] font-brand text-sm font-semibold text-foreground` |
 * | Field shell | `bg --surface-card; border 1.5px --border-default; --radius-md; height 52px; padding 0 14px; gap 10px` | `h-[52px] gap-[10px] rounded-md border-[1.5px] border-border bg-card px-[14px]` |
 * | Leading icon | `18px; --text-subtle` | `h-[18px] w-[18px] text-text-subtle` |
 * | Textarea | `min-height 96px; padding 13px 14px; resize none; --leading-normal` | `min-h-[96px] px-[14px] py-[13px] resize-none leading-normal` |
 * | Chip row | `display:flex; flex-wrap:wrap; gap:9px` | `flex flex-wrap gap-[9px]` |
 * | Chip | `padding:9px 15px; pill; 1.5px border; font-brand 600 --text-sm` | `ToggleChip density="member"` |
 *
 * The subtitle's bottom margin is **not** shared — it is `18px` on `rs0`, `20px` on `rs2` and
 * `22px` on `rs1`, `rs3` and `rs4`. See `SUBTITLE_GAP` in `registration-wizard-page.tsx`.
 *
 * ### `rs0` — First, the basics
 *
 * Google button `height 52px`, `1.5px` border, `--radius-md`, `gap 10px`, full width. Divider row
 * `gap 12px; margin 16px 0`, its label `--text-xs`, weight 600, `--text-subtle`. Two two-up rows
 * (`display:flex; gap:12px; margin-bottom:14px`), then email, then password with a trailing eye.
 *
 * ### `rs1` — Pick 3–5 interests
 *
 * Chip row, then the count pill: `margin-top:18px`, `--purple-50` background, `--brand-primary`
 * text, pill radius, `padding:7px 13px`, `--text-xs`, weight 600, `gap:7px`, a 13px check icon.
 *
 * ### `rs2` — What are you working toward?
 *
 * `Current goal` label over the textarea, the whole `<label>` carrying `margin-bottom:22px`. Then
 * the second question as a plain `<div>` — `font-brand; weight 600; --text-sm; --text-strong;
 * margin-bottom:4px` — over `Pick all that apply.` at `margin:0 0 12px; --text-xs; --text-muted`,
 * then the intent chip row.
 *
 * ### `rs3` — How do you like to connect?
 *
 * Format cards in a `flex-direction:column; gap:10px` stack (`SelectableCard control="radio"`),
 * then `margin-top:22px` to the LinkedIn block: label with a `· optional` suffix in
 * `--text-subtle` weight 500, the 52px field shell with a `#0A66C2` LinkedIn glyph, and a helper
 * at `margin:8px 2px 0; --text-xs; --text-subtle`.
 *
 * ### `rs4` — You're in control
 *
 * A `flex-direction:column; gap:14px` stack of `SelectableCard control="checkbox"`.
 *
 * ## Deliberate deviations
 *
 * **Fields are real inputs.** The prototype renders most of `rs0` as `<span>`s holding placeholder
 * text — it is a wireframe, not a form. The measurements are its; the semantics are ours, which is
 * what the bundle's own `README.md` asks for ("recreate them pixel-perfectly … don't copy the
 * prototype's internal structure").
 *
 * **The intent chips carry no icon.** `intentItems` gives each intent a lucide glyph
 * (`hire`→`user-plus`, `partner`→`handshake`, `learn`→`book-open`, `advise`→`lightbulb`,
 * `advised`→`compass`, `meet`→`users`) against a hard-coded six-item list. The real vocabulary is
 * the platform taxonomy (`ADM-001`), whose terms carry `termId`, `displayName` and `description`
 * and **no icon attribute** — so an icon here would mean a slug-to-glyph map that breaks silently
 * the first time an administrator adds a seventh intent or renames one. Recorded rather than
 * approximated; giving taxonomy terms an icon is its own story.
 *
 * **Vocabularies are read from the taxonomy, not from the prototype's literals.** `INT` in the
 * markup is ten hard-coded interest names and `fmtDefs` three hard-coded formats. Those are
 * `ADM-001`'s to author, so the chips and cards render whatever is seeded; the prototype's lists
 * are what the seed happens to contain, not a source.
 */

type Answers = MemberRegistrationAnswers;
type FieldErrors = Readonly<Record<string, string>>;

interface StepProps {
  readonly answers: Answers;
  readonly errors: FieldErrors;
  readonly onChange: (patch: Answers) => void;
  readonly vocabularies: RegistrationVocabularies | undefined;
}

/** The prototype's field shell — one definition, so no two fields on this wizard disagree. */
function Field({
  label,
  hint,
  optional,
  error,
  children,
  htmlFor,
}: {
  readonly label: string;
  readonly hint?: string;
  readonly optional?: boolean;
  readonly error?: string | undefined;
  readonly children: React.ReactNode;
  readonly htmlFor: string;
}): React.ReactElement {
  return (
    <label className="block min-w-0 flex-1" htmlFor={htmlFor}>
      <span className="mb-[7px] block font-brand text-sm font-semibold text-foreground">
        {label}
        {optional === true ? <span className="font-medium text-text-subtle"> · optional</span> : null}
      </span>
      {hint !== undefined ? (
        <span className="mb-2 block text-xs text-muted-foreground">{hint}</span>
      ) : null}
      {children}
      {/* `role="alert"` so the failure is announced, and the message sits with the control it
          belongs to rather than in a banner at the top (`MEM-001` AC-3). */}
      {error !== undefined ? (
        <span role="alert" className="mt-[6px] block text-xs font-semibold text-destructive">
          {error}
        </span>
      ) : null}
    </label>
  );
}

const SHELL =
  'flex h-[52px] w-full items-center gap-[10px] rounded-md border-[1.5px] bg-card px-[14px] text-md text-text-body outline-none focus-within:border-primary';

function shellClass(hasError: boolean): string {
  return `${SHELL} ${hasError ? 'border-destructive' : 'border-border'}`;
}

const BARE_INPUT = 'min-w-0 flex-1 border-none bg-transparent outline-none placeholder:text-text-subtle';

/** The textarea, at `rs2`'s measurements. Both of the wizard's textareas would be one control if
 * there were still two; there is one, and it keeps the shared definition so `MEM-009`'s goal field
 * can adopt it unchanged. */
function textareaClass(hasError: boolean): string {
  return `min-h-[96px] w-full resize-none rounded-md border-[1.5px] bg-card px-[14px] py-[13px] text-md leading-normal text-text-body outline-none placeholder:text-text-subtle focus:border-primary ${
    hasError ? 'border-destructive' : 'border-border'
  }`;
}

/* ============================================================================
   rs0 — First, the basics
   ============================================================================ */

export function BasicsStep({
  answers,
  errors,
  onChange,
  vocabularies,
  email,
  password,
  onEmailChange,
  onPasswordChange,
  googleLinked,
  signedInAs,
  profileAsk,
}: StepProps & {
  readonly email: string;
  readonly password: string;
  readonly onEmailChange: (value: string) => void;
  readonly onPasswordChange: (value: string) => void;
  readonly googleLinked: boolean;
  /**
   * The address on the session, when there is one (`MEM-002` AC-7, `MEM-001` AC-7).
   *
   * Present means **the whole account half of this step is replaced by a statement of who is
   * signed in**: no Google button, no divider, no email field, no password field and no password
   * rule. There is no account to create, so offering to create one would be asking somebody to
   * type a credential into a form for no reason — which is the shape of every credential-harvesting
   * page there has ever been, and worth not teaching members to expect.
   *
   * Role and company stay, for `MEM-002` AC-3's reason applied to a different door: an account
   * carries a name and an address and carries neither, and those two are most of what another
   * member sees on a preview card. An existing account shortens step 1; it does not skip it.
   */
  readonly signedInAs?: string | undefined;
  /**
   * The ask, when this account has no member profile at all (`USR-014` AC-2, AC-5).
   *
   * Present only for `member_profile_incomplete` — an organizer or an administrator who came to
   * the Member App to attend something — and only alongside {@link signedInAs}, because an account
   * is what it is an ask about. A member half-way through the wizard gets nothing here: they are
   * resuming, not being asked.
   *
   * **Every sentence is the server's** (`domains/users/member-app-admission.ts`). AC-5 has three
   * requirements — say why, say what it unlocks, and do not imply their organizer or administrator
   * access is affected — and they are one decision rather than three strings a screen assembles,
   * so the API refusing a seat and this panel say the same thing in the same words.
   */
  readonly profileAsk?:
    | { readonly heading: string; readonly body: string; readonly reassurance: string }
    | undefined;
}): React.ReactElement {
  const [revealed, setRevealed] = React.useState(false);
  const signedIn = signedInAs !== undefined;
  return (
    <>
      {signedIn ? (
        // The account, stated rather than collected. `--radius-md` and the 1.5px border are the
        // field shell's, so this reads as part of the form rather than as a banner over it.
        <div className="mb-4 rounded-md border-[1.5px] border-border bg-card px-[14px] py-3">
          <p className="flex items-center gap-[10px] font-brand text-sm font-semibold text-foreground">
            <MailIcon />
            <span className="text-text-subtle">{REGISTRATION_SIGNED_IN_LABEL}</span>
            <span className="min-w-0 flex-1 truncate">{signedInAs}</span>
          </p>
          <p className="mt-2 text-xs text-muted-foreground">{REGISTRATION_SIGNED_IN_NOTE}</p>
          {/*
            `USR-014` AC-5. Inside the account card rather than above the form, because it is a
            sentence *about this account* and the card is where this account is on screen — and
            below the note, so somebody who already knows why they are here reads the shorter one
            first. No icon, no colour, no border: the visual language of a warning would say the
            thing the words are careful not to.
          */}
          {profileAsk === undefined ? null : (
            <div className="mt-3 border-t border-divider pt-3">
              <p className="font-brand text-sm font-semibold text-foreground">
                {profileAsk.heading}
              </p>
              <p className="mt-1 text-xs text-muted-foreground">{profileAsk.body}</p>
              <p className="mt-2 text-xs text-muted-foreground">{profileAsk.reassurance}</p>
            </div>
          )}
        </div>
      ) : (
        /*
          Google, then `or sign up with email` — **only when the API offers Google** (TW 44131761).

          This button used to be drawn unconditionally and sent the browser to
          `/api/auth/social/google/signup-start` on the site's own origin, which is not the API and
          answers JSON where it answers at all. The member sign-in has always asked the API which
          providers exist (`SocialSignInButtons`), and on dev the answer is none — no Google client
          is registered (`USR-002`, external setup) — so the two screens disagreed. Both now use
          the same component: no provider, no button and no divider; a provider, the real flow,
          returning to this wizard signed in.
        */
        <SocialSignupButtons
          callbackPath="/app/auth/callback"
          next="/app/join"
          showHeading={false}
          renderProvider={(provider) => (
            // The Google button, transcribed: full width, 52px, 1.5px border, --radius-md, gap 10px.
            <button
              type="button"
              onClick={provider.onClick}
              disabled={provider.disabled}
              className="flex h-[52px] w-full items-center justify-center gap-[10px] rounded-md border-[1.5px] border-border bg-card font-brand text-md font-bold text-foreground disabled:opacity-60"
            >
              <GoogleMark />
              {googleLinked
                ? 'Google connected'
                : provider.busy
                  ? 'Redirecting…'
                  : `Continue with ${provider.displayName}`}
            </button>
          )}
          after={
            // `or sign up with email`: divider · label · divider, gap 12px, margin 16px 0.
            <div className="my-4 flex items-center gap-3">
              <span className="h-px flex-1 bg-divider" />
              <span className="text-xs font-semibold text-text-subtle">or sign up with email</span>
              <span className="h-px flex-1 bg-divider" />
            </div>
          }
        />
      )}

      <div className="mb-[14px] flex gap-3">
        <Field label="First name" htmlFor="firstName" error={errors['firstName']}>
          <span className={shellClass(errors['firstName'] !== undefined)}>
            <UserIcon />
            <input
              id="firstName"
              name="firstName"
              autoComplete="given-name"
              maxLength={MEMBER_REGISTRATION_FIELD_LIMITS.firstName.max}
              className={BARE_INPUT}
              placeholder="Deb"
              value={answers.firstName ?? ''}
              onChange={(event) => onChange({ firstName: event.target.value })}
            />
          </span>
        </Field>
        <Field label="Last name" htmlFor="lastName" error={errors['lastName']}>
          <span className={shellClass(errors['lastName'] !== undefined)}>
            <input
              id="lastName"
              name="lastName"
              autoComplete="family-name"
              maxLength={MEMBER_REGISTRATION_FIELD_LIMITS.lastName.max}
              className={BARE_INPUT}
              placeholder="Hamilton"
              value={answers.lastName ?? ''}
              onChange={(event) => onChange({ lastName: event.target.value })}
            />
          </span>
        </Field>
      </div>

      <div className="mb-[14px] flex gap-3">
        <Field label="Role" htmlFor="role" error={errors['role']}>
          <span className={shellClass(errors['role'] !== undefined)}>
            <BriefcaseIcon />
            <input
              id="role"
              name="role"
              autoComplete="organization-title"
              maxLength={MEMBER_REGISTRATION_FIELD_LIMITS.role.max}
              className={BARE_INPUT}
              placeholder="Founder"
              value={answers.role ?? ''}
              onChange={(event) => onChange({ role: event.target.value })}
            />
          </span>
        </Field>
        <Field label="Company" htmlFor="company" error={errors['company']}>
          <span className={shellClass(errors['company'] !== undefined)}>
            <BuildingIcon />
            <input
              id="company"
              name="company"
              autoComplete="organization"
              maxLength={MEMBER_REGISTRATION_FIELD_LIMITS.company.max}
              className={BARE_INPUT}
              placeholder="H2HSpark"
              value={answers.company ?? ''}
              onChange={(event) => onChange({ company: event.target.value })}
            />
          </span>
        </Field>
      </div>

      {/* Not asked of somebody who is signed in: the address is on the session, and is stated
          above rather than collected. */}
      {signedIn ? null : (
        <div className="mb-[14px]">
          <Field label="Email address" htmlFor="email" error={errors['email']}>
            <span className={shellClass(errors['email'] !== undefined)}>
              <MailIcon />
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                className={BARE_INPUT}
                placeholder="you@example.com"
                value={email}
                onChange={(event) => onEmailChange(event.target.value)}
              />
            </span>
          </Field>
        </div>
      )}

      {/* Hidden on the Google path — `MEM-002` AC-3's "shortens step 1". Role and company above
          stay, because Google supplies neither. Hidden for a signed-in visitor too, and for a
          stronger reason: there is no account to give a password to. */}
      {googleLinked || signedIn ? null : (
        <>
          <Field label="Password" htmlFor="password" error={errors['password']}>
            <span className={shellClass(errors['password'] !== undefined)}>
              <LockIcon />
              <input
                id="password"
                aria-describedby="password-rule"
                name="password"
                type={revealed ? 'text' : 'password'}
                autoComplete="new-password"
                className={BARE_INPUT}
                placeholder="Create a password"
                value={password}
                onChange={(event) => onPasswordChange(event.target.value)}
              />
              <button
                type="button"
                onClick={() => setRevealed((value) => !value)}
                aria-label={revealed ? 'Hide password' : 'Show password'}
                className="text-text-subtle"
              >
                <EyeIcon />
              </button>
            </span>
          </Field>
          {/* The password rule, directly under the field it is about (TW 44132998). It sat at the
              foot of the step, below Industry and Career stage, where it read as a rule for those.
              Drawn only with the field: signed in there is no password to choose, and on the Google
              path there is none either. */}
          <p id="password-rule" className="mt-[6px] text-xs text-text-subtle">
            {REGISTRATION_PASSWORD_RULE}
          </p>
        </>
      )}
      {/* `MEM-045` AC-1/AC-3. Beside role and company because that is where the professional
          context already is — the specification's row is "Professional role & industry", one data
          point in one sentence. Optional, unlike the four fields above them. */}
      <MatchingFieldsPanel
        mountPoint="registration-basics"
        values={answers as Record<string, string | undefined>}
        terms={vocabularies?.terms ?? {}}
        onChange={(attribute, value) => onChange({ [attribute]: value })}
      />

    </>
  );
}

/* ============================================================================
   rs1 — Pick 3–5 interests
   ============================================================================ */

/**
 * Step 2 (`MEM-003` AC-2, AC-5).
 *
 * **The step's heading is the question**, so there is no `legend` here — `rs1` is `h1` · subtitle ·
 * chip row · count pill and nothing else. Rendering a second "Pick 3–5 interests" label under the
 * `h1` that already says it would be the wizard repeating itself.
 */
export function InterestsStep({ answers, errors, onChange, vocabularies }: StepProps): React.ReactElement {
  const interests = termsOf(vocabularies, 'interest-tag');
  const chosen = answers.interestTagTermIds ?? [];
  const atMax = chosen.length >= MEMBER_INTEREST_TAG_SELECTION.max;

  return (
    <>
      <ChipGroup
        ariaLabel={`Pick ${String(MEMBER_INTEREST_TAG_SELECTION.min)} to ${String(MEMBER_INTEREST_TAG_SELECTION.max)} interests`}
        error={errors['interestTagTermIds']}
        terms={interests}
        selected={chosen}
        onToggle={(termId) =>
          onChange({
            interestTagTermIds: chosen.includes(termId)
              ? chosen.filter((id) => id !== termId)
              : [...chosen, termId],
          })
        }
        mode="multi"
        // The sixth chip is disabled rather than accepted-then-refused, so the limit is visible
        // in the control rather than only in a validation message.
        disableUnselected={atMax}
      />
      {/* `{{ interestCount }} selected`: margin-top 18px, --purple-50 pill, 7px 13px, text-xs 600,
          gap 7px, 13px check. `aria-live` so the count a sighted member watches change is also
          announced — the prototype has no equivalent because a wireframe has no screen reader. */}
      <div
        aria-live="polite"
        className="mt-[18px] inline-flex items-center gap-[7px] rounded-pill bg-brand-50 px-[13px] py-[7px] text-xs font-semibold text-primary"
      >
        <Check className="h-[13px] w-[13px]" aria-hidden />
        {chosen.length} selected
      </div>
    </>
  );
}

/* ============================================================================
   rs2 — What are you working toward?
   ============================================================================ */

/**
 * Step 3 (`MEM-003` AC-2). **Two questions on one step**, as `rs2` has them — the goal, then the
 * connection intents under their own sub-heading.
 *
 * `currentGoal` is optional per `MEM-003`'s reference table even though it is the step's headline
 * question; see `goalAnswersSchema`'s note. The label carries no `· optional` suffix, because the
 * prototype's does not and the field is the reason the step exists.
 */
export function GoalStep({ answers, errors, onChange, vocabularies }: StepProps): React.ReactElement {
  const intents = termsOf(vocabularies, 'connection-intent');
  const goal = answers.currentGoal ?? '';

  return (
    <>
      {/* `<label style="display:block;margin-bottom:22px">` */}
      <div className="mb-[22px]">
        <Field label="Current goal" htmlFor="currentGoal" error={errors['currentGoal']}>
          <textarea
            id="currentGoal"
            name="currentGoal"
            maxLength={MEMBER_REGISTRATION_FIELD_LIMITS.currentGoal.max}
            className={textareaClass(errors['currentGoal'] !== undefined)}
            placeholder="I am currently building / exploring / seeking…"
            value={goal}
            onChange={(event) => onChange({ currentGoal: event.target.value })}
          />
          <span className="mt-[6px] block text-right text-xs text-text-subtle">
            {goal.length}/{MEMBER_REGISTRATION_FIELD_LIMITS.currentGoal.max}
          </span>
        </Field>
      </div>

      <fieldset>
        {/* The prototype's sub-heading: font-brand 600 --text-sm --text-strong, margin-bottom 4px,
            over `Pick all that apply.` at margin 0 0 12px, --text-xs, --text-muted. */}
        <legend className="mb-1 font-brand text-sm font-semibold text-foreground">
          What kind of connection are you seeking?
        </legend>
        <p className="mb-3 text-xs text-muted-foreground">Pick all that apply.</p>
        <ChipGroup
          ariaLabel="What kind of connection are you seeking?"
          error={errors['connectionIntentTermIds']}
          terms={intents}
          selected={answers.connectionIntentTermIds ?? []}
          onToggle={(termId) => {
            const current = answers.connectionIntentTermIds ?? [];
            onChange({
              connectionIntentTermIds: current.includes(termId)
                ? current.filter((id) => id !== termId)
                : [...current, termId],
            });
          }}
          mode="multi"
        />
      </fieldset>

      {/* `MEM-045` AC-1/AC-3. Same register as the goal above it — one free-text sentence about
          what the member is preoccupied with — which is why the client put it on this step. */}
      <MatchingFieldsPanel
        mountPoint="registration-goal"
        values={answers as Record<string, string | undefined>}
        terms={vocabularies?.terms ?? {}}
        onChange={(attribute, value) => onChange({ [attribute]: value })}
      />
    </>
  );
}

/* ============================================================================
   rs3 — How do you like to connect?
   ============================================================================ */

/**
 * Step 4 (`MEM-003` AC-3, in part).
 *
 * Format is **single-select** — `fmtCards` compares `this.state.fmt === id` and draws a radio dot,
 * and `MEM-009` AC-6 says "exactly one always selected" from the other end.
 */
export function ConnectingStep({ answers, errors, onChange, vocabularies }: StepProps): React.ReactElement {
  const formats = termsOf(vocabularies, 'connection-format');

  return (
    <>
      {/* `display:flex;flex-direction:column;gap:10px` */}
      {formats === undefined ? (
        <VocabularyLoading shape="cards" />
      ) : formats.length === 0 ? (
        <VocabularyNotReady />
      ) : (
        <div
          role="radiogroup"
          aria-label="How do you like to connect?"
          className="flex flex-col gap-[10px]"
        >
          {formats.map((term) => (
            <SelectableCard
              key={term.termId}
              control="radio"
              name="connectionFormat"
              selected={answers.connectionFormatTermId === term.termId}
              onSelect={() => onChange({ connectionFormatTermId: term.termId })}
              label={term.displayName}
              description={term.description}
            />
          ))}
        </div>
      )}
      {errors['connectionFormatTermId'] !== undefined ? (
        <p role="alert" className="mt-[6px] text-xs font-semibold text-destructive">
          {errors['connectionFormatTermId']}
        </p>
      ) : null}

      {/* `MEM-045` AC-1/AC-3. This step already asks how a member wants to connect; social
          comfort is the same question asked about temperament rather than about format. */}
      <MatchingFieldsPanel
        mountPoint="registration-connecting"
        values={answers as Record<string, string | undefined>}
        terms={vocabularies?.terms ?? {}}
        onChange={(attribute, value) => onChange({ [attribute]: value })}
      />

      {/* `<div style="margin-top:22px">` — LinkedIn, optional. */}
      <div className="mt-[22px]">
        <Field label="LinkedIn profile" optional htmlFor="linkedIn" error={errors['linkedIn']}>
          <span className={shellClass(errors['linkedIn'] !== undefined)}>
            <LinkedInIcon />
            <input
              id="linkedIn"
              name="linkedIn"
              className={BARE_INPUT}
              placeholder="linkedin.com/in/yourname"
              value={answers.linkedIn ?? ''}
              onChange={(event) => onChange({ linkedIn: event.target.value })}
            />
          </span>
        </Field>
        {/* `margin:8px 2px 0` */}
        <p className="mt-2 px-[2px] text-xs text-text-subtle">
          Helps us make better matches and gives connections a way to find you.
        </p>
      </div>
    </>
  );
}

/* ============================================================================
   rs4 — You're in control
   ============================================================================ */

/**
 * Step 5 (`MEM-003` AC-3, in part; collected here, given meaning by `MEM-010`).
 *
 * The three cards' labels and descriptions are `optDefs` verbatim. The third one's wording is the
 * prototype's — "Allow post-event introductions" — where the four-step wizard had written "Send me
 * follow-up reminders after an event". They are the same election
 * (`receivePostEventFollowUpNotifications`); the prototype wins on presentation.
 */
export function PrivacyStep({ answers, onChange }: Omit<StepProps, 'vocabularies'>): React.ReactElement {
  return (
    // `display:flex;flex-direction:column;gap:14px`
    <div className="flex flex-col gap-[14px]">
      <SelectableCard
        control="checkbox"
        selected={answers.appearInAttendeePreview === true}
        onSelect={() => onChange({ appearInAttendeePreview: answers.appearInAttendeePreview !== true })}
        label="Show me in attendee preview"
        description="Members can see your name & interests 72h before an event"
      />
      <SelectableCard
        control="checkbox"
        selected={answers.receivePreEventMatchSuggestions === true}
        onSelect={() =>
          onChange({ receivePreEventMatchSuggestions: answers.receivePreEventMatchSuggestions !== true })
        }
        label="Send me pre-event match suggestions"
        description="3–5 curated people to meet, 48h before"
      />
      <SelectableCard
        control="checkbox"
        selected={answers.receivePostEventFollowUpNotifications === true}
        onSelect={() =>
          onChange({
            receivePostEventFollowUpNotifications: answers.receivePostEventFollowUpNotifications !== true,
          })
        }
        label="Allow post-event introductions"
        description="We can reconnect you with people you missed"
      />
    </div>
  );
}

/* ============================================================================
   Shared pieces
   ============================================================================ */

/**
 * A row of selectable chips over a taxonomy vocabulary.
 *
 * Renders the not-ready state in place when the vocabulary is empty (`MEM-004` AC-2) — the caller
 * passes an empty array and gets an explanation rather than an empty row, which is precisely the
 * "empty control" the story forbids.
 *
 * **No `legend` of its own.** Both callers already have a heading above the row — the step's `h1`
 * on `rs1`, the sub-heading on `rs2` — so this takes an `ariaLabel` and lets the caller own the
 * visible text. That is what stopped the interests step rendering its question twice.
 */
function ChipGroup({
  ariaLabel,
  error,
  terms,
  selected,
  onToggle,
  mode,
  disableUnselected = false,
}: {
  readonly ariaLabel: string;
  readonly error: string | undefined;
  /** `undefined` while the vocabularies are still loading; `[]` once they have and it is empty. */
  readonly terms: ReadonlyArray<{ readonly termId: string; readonly displayName: string }> | undefined;
  readonly selected: ReadonlyArray<string>;
  readonly onToggle: (termId: string) => void;
  readonly mode: 'single' | 'multi';
  readonly disableUnselected?: boolean;
}): React.ReactElement {
  return (
    <>
      {terms === undefined ? (
        <VocabularyLoading shape="chips" />
      ) : terms.length === 0 ? (
        <VocabularyNotReady />
      ) : (
        <div
          className="flex flex-wrap gap-[9px]"
          // A single-select row is a radio group and a multi-select row is not. Both look the
          // same; the role is the only thing telling a screen-reader user whether picking a
          // second chip keeps the first.
          role={mode === 'single' ? 'radiogroup' : 'group'}
          aria-label={ariaLabel}
        >
          {terms.map((term) => {
            const isSelected = selected.includes(term.termId);
            return (
              <ToggleChip
                key={term.termId}
                density="member"
                selection={mode}
                checked={isSelected}
                disabled={disableUnselected && !isSelected}
                onChange={() => onToggle(term.termId)}
              >
                {term.displayName}
              </ToggleChip>
            );
          })}
        </div>
      )}
      {error !== undefined ? (
        <p role="alert" className="mt-[6px] text-xs font-semibold text-destructive">
          {error}
        </p>
      ) : null}
    </>
  );
}

/**
 * One vocabulary's terms, or `undefined` while the vocabularies are still on their way.
 *
 * The two have to stay apart: `[]` is a vocabulary an administrator has not populated, which the
 * step says in words (`VocabularyNotReady`), and `undefined` is a request in flight, which it
 * must not. Folding both into `[]` told every new member a step was not ready for the moment
 * before its chips arrived (TW 44261152).
 */
function termsOf(
  vocabularies: RegistrationVocabularies | undefined,
  vocabulary: string,
): RegistrationVocabularies['terms'][string] | undefined {
  if (vocabularies === undefined) return undefined;
  return vocabularies.terms[vocabulary] ?? [];
}

/**
 * The choices are still loading (TW 44261152): grey bars where the chips or cards will go, the
 * same `Skeleton` the rest of the app loads with. No words on screen, because there is nothing to
 * say yet; a screen reader hears that the choices are loading.
 */
function VocabularyLoading({ shape }: { readonly shape: 'chips' | 'cards' }): React.ReactElement {
  return (
    <div role="status" aria-busy="true">
      <span className="sr-only">Loading the choices for this step…</span>
      {shape === 'chips' ? (
        <div className="flex flex-wrap gap-[9px]" aria-hidden>
          {[112, 88, 136, 96, 120, 80].map((width) => (
            <Skeleton key={width} className="h-10 rounded-pill" style={{ width }} />
          ))}
        </div>
      ) : (
        <div className="flex flex-col gap-[10px]" aria-hidden>
          {[0, 1, 2].map((index) => (
            <Skeleton key={index} className="h-[72px] w-full rounded-lg" />
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * The vocabulary-not-ready state (`MEM-004` AC-2, AC-3).
 *
 * Deliberately **not** styled as an error — no red, no warning icon, no `role="alert"`. Nothing
 * has gone wrong that the member did, and `MEM-004` AC-3 requires that they are not blamed. The
 * copy is imported rather than written here, so the words the member reads and the words the test
 * asserts are one string.
 */
export function VocabularyNotReady(): React.ReactElement {
  return (
    <div className="rounded-lg border-[1.5px] border-border-subtle bg-card p-4">
      <p className="font-brand text-sm font-bold text-foreground">{NOT_READY.heading}</p>
      <p className="mt-[6px] text-xs leading-normal text-muted-foreground">{NOT_READY.body}</p>
      <p className="mt-2 text-xs leading-normal text-muted-foreground">{NOT_READY.nextStep}</p>
    </div>
  );
}

/* ---- Icons, at the prototype's sizes ------------------------------------------------------ */

const ICON = 'h-[18px] w-[18px] flex-none text-text-subtle';

function UserIcon(): React.ReactElement {
  return (
    <svg className={ICON} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  );
}

function BriefcaseIcon(): React.ReactElement {
  return (
    <svg className={ICON} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <rect width="20" height="14" x="2" y="7" rx="2" />
      <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
    </svg>
  );
}

function BuildingIcon(): React.ReactElement {
  return (
    <svg className={ICON} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M6 22V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v18Z" />
      <path d="M10 6h4M10 10h4M10 14h4M10 18h4" />
    </svg>
  );
}

function MailIcon(): React.ReactElement {
  return (
    <svg className={ICON} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <rect width="20" height="16" x="2" y="4" rx="2" />
      <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
    </svg>
  );
}

function LockIcon(): React.ReactElement {
  return (
    <svg className={ICON} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <rect width="18" height="11" x="3" y="11" rx="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
  );
}

function EyeIcon(): React.ReactElement {
  return (
    <svg className="h-[18px] w-[18px]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

/** LinkedIn keeps its brand colour, as the prototype has it (`color:#0A66C2`). */
function LinkedInIcon(): React.ReactElement {
  return (
    <svg className="h-[18px] w-[18px] flex-none text-[#0A66C2]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-4 0v7h-4v-7a6 6 0 0 1 6-6z" />
      <rect width="4" height="12" x="2" y="9" />
      <circle cx="4" cy="4" r="2" />
    </svg>
  );
}

/** The Google mark, verbatim from the prototype's inline SVG. */
function GoogleMark(): React.ReactElement {
  return (
    <svg width="20" height="20" viewBox="0 0 48 48" className="flex-none" aria-hidden>
      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
      <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
    </svg>
  );
}
